// Event Filter - Optimized Version
class EventFilter {
  constructor() {
    this.archiveFilter = document.querySelector('.archive-filter');
    this.debounceTimer = null;
    this.isLoading = false;
    this.cache = new Map();

    if (!this.archiveFilter) {
      return;
    }

    this.init();
  }

  init() {
    // Rebind the current form element after any DOM updates
    this.archiveFilter = document.querySelector('.archive-filter');
    if (!this.archiveFilter) {
      return;
    }

    this.initializeRadioStates();
    this.setupEventListeners();
    this.setupDateInputHandling();
    this.setupCustomDateToggle();
  }

  initializeRadioStates() {
    const checkedRadios = this.archiveFilter.querySelectorAll(
      'input[type="radio"]:checked'
    );
    checkedRadios.forEach(radio => {
      radio.setAttribute('data-was-selected', 'true');
    });
  }

  setupEventListeners() {
    // Form submission
    this.archiveFilter.addEventListener('submit', e => {
      e.preventDefault();
      this.executeFilter();
    });

    // Change events with debouncing
    this.archiveFilter.addEventListener('change', e => {
      if (e.target.type === 'checkbox' || e.target.type === 'radio') {
        this.debouncedFilter();
      }
    });

    // Click events for deselection
    this.archiveFilter.addEventListener('click', e => {
      this.handleClickEvent(e);
    });
  }

  setupDateInputHandling() {
    const dateInput = this.archiveFilter.querySelector('input[type="date"]');
    if (!dateInput) {
      return;
    }

    // Single event listener with debouncing
    const handleDateChange = () => this.debouncedFilter();

    dateInput.addEventListener('change', handleDateChange);
    dateInput.addEventListener('input', handleDateChange);
    dateInput.addEventListener('blur', handleDateChange);
  }

  setupCustomDateToggle() {
    const inputPeriod = this.archiveFilter.querySelectorAll(
      'input[name="ec_period"]'
    );
    const customDate = this.archiveFilter.querySelector('.custom-date');

    if (!customDate) {
      return;
    }

    inputPeriod.forEach(input => {
      input.addEventListener('click', e => {
        if (e.target.value === 'custom') {
          customDate.classList.remove('hidden');
        } else {
          customDate.classList.add('hidden');
        }
      });
    });
  }

  handleClickEvent(e) {
    if (e.target.type === 'radio') {
      this.handleRadioClick(e);
    }
  }

  handleRadioClick(e) {
    const wasAlreadySelected = e.target.hasAttribute('data-was-selected');

    if (wasAlreadySelected && e.target.checked) {
      // Deselect current radio
      e.preventDefault();
      e.target.checked = false;
      e.target.removeAttribute('data-was-selected');
      this.debouncedFilter();
    } else {
      // Clear all radio states in group
      const radioGroup = this.archiveFilter.querySelectorAll(
        `input[name="${e.target.name}"]`
      );
      radioGroup.forEach(radio => radio.removeAttribute('data-was-selected'));

      // Mark new selection after browser handles click
      setTimeout(() => {
        if (e.target.checked) {
          e.target.setAttribute('data-was-selected', 'true');
        }
      }, 0);
    }
  }

  handleCheckboxClick(e) {
    // No-op: rely on native change event to trigger filtering
  }

  debouncedFilter() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.executeFilter();
    }, 300); // 300ms debounce
  }

  async executeFilter() {
    if (this.isLoading) {
      return;
    }

    try {
      this.setLoadingState(true);

      const formData = new FormData(this.archiveFilter);
      const selectedPeriod = formData.get('ec_period');

      // Only include date parameter if custom period is selected
      if (selectedPeriod !== 'custom') {
        formData.delete('ec_date');
      }

      const queryString = new URLSearchParams(formData).toString();
      const url = `${this.archiveFilter.action}?${queryString}`;

      // Check cache first
      if (this.cache.has(url)) {
        this.updateContent(this.cache.get(url));
        this.setLoadingState(false);
        return;
      }

      // Fetch new content
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parsedContent = this.parseResponse(html);

      // Cache the result
      this.cache.set(url, parsedContent);

      // Update content
      this.updateContent(parsedContent);

      // Update URL
      history.pushState({}, '', url);
    } catch (error) {
      console.error('Filter error:', error);
      this.showError(
        'Er is een fout opgetreden bij het filteren. Probeer het opnieuw.'
      );
    } finally {
      this.setLoadingState(false);
    }
  }

  parseResponse(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    return {
      archiveContent: temp.querySelector('.archive-content')?.innerHTML || '',
      formContent: temp.querySelector('.col-12.col-md-3')?.innerHTML || '',
    };
  }

  updateContent(content) {
    const archiveContainer = document.querySelector('.archive-content');
    const formContainer = document.querySelector('.col-12.col-md-3');

    if (archiveContainer && content.archiveContent) {
      archiveContainer.innerHTML = content.archiveContent;
    }

    if (formContainer && content.formContent) {
      formContainer.innerHTML = content.formContent;
      // Reinitialize after content update
      this.init();
    }
  }

  setLoadingState(loading) {
    this.isLoading = loading;
    const containers = document.querySelectorAll(
      '.archive-content, .col-12.col-md-3'
    );

    containers.forEach(container => {
      if (loading) {
        container.classList.add('loading');
        container.setAttribute('aria-busy', 'true');
      } else {
        container.classList.remove('loading');
        container.removeAttribute('aria-busy');
      }
    });
  }

  showError(message) {
    const archiveContainer = document.querySelector('.archive-content');
    if (archiveContainer) {
      archiveContainer.innerHTML = `
        <div class="error-message" role="alert">
          <p>${message}</p>
          <button onclick="location.reload()" class="button button-primary">
            Pagina vernieuwen
          </button>
        </div>
      `;
    }
  }

  // Cleanup method for potential memory management
  destroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.cache.clear();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new EventFilter();
});
